import { create } from 'zustand'
// P0 修复: 移除 persist，Token 改为 HttpOnly Cookie
import { getUserProfile, updateUserProfile, type UserProfile } from '@/services/user'
import { sendVerificationCode, verifyCodeAndLogin, loginWithPasswordApi, logoutApi, type SendCodeResponse } from '@/services/auth'
import { logger, errorHandler } from '@/utils/logger'

interface UserState {
  user: UserProfile | null
  isLoading: boolean
  error: string | null
  isAuthenticated: boolean
  isAuthChecked: boolean  // P0-6 新增：认证检查是否完成

  // Auth methods
  loginWithPhone: (phoneNumber: string, code: string) => Promise<void>
  loginWithPassword: (identifier: string, password: string) => Promise<void>
  sendVerificationCode: (phoneNumber: string, purpose?: 'login' | 'password_reset') => Promise<SendCodeResponse>
  logout: () => Promise<void>
  checkAuth: () => Promise<boolean>

  // User methods
  updateUser: (data: Partial<UserProfile>) => Promise<void>
}

export const useUserStore = create<UserState>()(
  (set) => ({
    // Initial state
    user: null,
    isLoading: false,
    error: null,
    isAuthenticated: false,
    isAuthChecked: false,  // P0-6 新增：初始状态为未检查

    // Auth: Send verification code
    sendVerificationCode: async (phoneNumber: string, purpose: 'login' | 'password_reset' = 'login') => {
      set({ isLoading: true, error: null })
      try {
        const data = await sendVerificationCode(phoneNumber, purpose)
        set({ isLoading: false })
        return data
      } catch (error) {
        errorHandler.handleSync(error, 'sendVerificationCode')
        set({ error: errorHandler.getUserMessage(error), isLoading: false })
        throw error
      }
    },

    // Auth: Login with phone verification code
    loginWithPhone: async (phoneNumber: string, code: string) => {
      set({ isLoading: true, error: null })
      try {
        // P0 修复: 登录接口现在只返回用户信息，Token 在 Cookie 中
        await verifyCodeAndLogin(phoneNumber, code)

        // 获取用户信息
        try {
          const user = await getUserProfile()
          set({
            user,
            isAuthenticated: true,
            isAuthChecked: true,
            isLoading: false
          })
        } catch (profileError) {
          errorHandler.handleSync(profileError, 'getUserProfile')
          throw profileError
        }
      } catch (error) {
        errorHandler.handleSync(error, 'loginWithPhone')
        set({ error: errorHandler.getUserMessage(error), isLoading: false, isAuthenticated: false, isAuthChecked: true })
        throw error
      }
    },

    // Auth: Login with password（手机号或邮箱）
    loginWithPassword: async (identifier: string, password: string) => {
      set({ isLoading: true, error: null })
      try {
        await loginWithPasswordApi(identifier, password)
        try {
          const user = await getUserProfile()
          set({
            user,
            isAuthenticated: true,
            isAuthChecked: true,
            isLoading: false
          })
        } catch (profileError) {
          errorHandler.handleSync(profileError, 'getUserProfile')
          throw profileError
        }
      } catch (error) {
        errorHandler.handleSync(error, 'loginWithPassword')
        set({ error: errorHandler.getUserMessage(error), isLoading: false, isAuthenticated: false, isAuthChecked: true })
        throw error
      }
    },

    // P0-6 修复: 检查认证状态（通过调用 /api/auth/me）
    checkAuth: async () => {
      try {
        const user = await getUserProfile()
        set({ user, isAuthenticated: true, isAuthChecked: true })
        return true
      } catch (_error) {
        // 401 或其他错误表示未认证
        set({ user: null, isAuthenticated: false, isAuthChecked: true })
        return false
      }
    },

    // Auth: Logout
    logout: async () => {
      try {
        // P0 修复: 调用后端登出接口清除 Cookie
        await logoutApi()
      } catch (error) {
        logger.warn('Logout API call failed:', error)
        // 即使 API 失败，也清除本地状态
      }
      
      set({
        user: null,
        isAuthenticated: false,
        isAuthChecked: true,  // 登出后也算检查完成
        error: null
      })
    },

    // User: Fetch user profile

    updateUser: async (data) => {
      set({ isLoading: true, error: null })
      try {
        const updatedUser = await updateUserProfile(data)
        set({ user: updatedUser, isLoading: false })
      } catch (error) {
        errorHandler.handleSync(error, 'updateUser')
        set({ error: errorHandler.getUserMessage(error), isLoading: false })
        throw error
      }
    }
  })
)

// Selector helpers

